// src/components/RoundRobinGame.js
import React, { useEffect, useMemo, useRef, useState } from 'react';
import SmartRanker from '../utils/SmartRanker';
import './RoundRobinGame.css';

/**
 * Pairwise ranking game backed by SmartRanker (transitive skip).
 * Identity: use item.id (unique). Titles are display-only.
 *
 * Props:
 *  items: [{ id: string, title: string }]
 *  listTitle: string
 *  onComplete: (finalOrderObjects: [{ id, title }]) => void
 */
const RoundRobinGame = ({ items = [], listTitle = '', onComplete }) => {
  // normalize inputs: stable id + trimmed title
  const safeItems = useMemo(
    () =>
      (items || []).map((it, i) => ({
        id: String(it?.id ?? i),
        title: String(it?.title ?? '').trim() || `Item ${i + 1}`,
      })),
    [items]
  );

  // maps for quick lookups
  const byId = useMemo(() => {
    const m = new Map();
    for (const it of safeItems) m.set(it.id, it);
    return m;
  }, [safeItems]);

  const rankerRef = useRef(null);
  const [pair, setPair] = useState(null); // [idA, idB]
  const [done, setDone] = useState(false);

  // init ranker
  useEffect(() => {
    if (!safeItems.length) return;
    const ids = safeItems.map(x => x.id);
    rankerRef.current = new SmartRanker(ids);
    const first = rankerRef.current.nextPair(); // [idA, idB] or null
    setPair(first);
    setDone(!first);
  }, [safeItems]);

  const advance = () => {
    const nxt = rankerRef.current?.nextPair() ?? null;
    if (nxt) {
      setPair(nxt);
    } else {
      setDone(true);
      const orderedIds = rankerRef.current?.generateRanking() ?? [];
      const finalObjects = orderedIds.map(id => byId.get(id)).filter(Boolean);
      onComplete?.(finalObjects);
    }
  };

  const choose = (winnerId, loserId) => {
    if (!rankerRef.current || !pair) return;
    rankerRef.current.recordResult(winnerId, loserId);
    advance();
  };

  if (!safeItems.length) {
    return (
      <div className="rr2-wrap">
        <h2 className="rr2-title">{listTitle}</h2>
        <div className="rr2-empty">No items to rank.</div>
      </div>
    );
  }

  const a = pair?.[0] ? byId.get(pair[0]) : null;
  const b = pair?.[1] ? byId.get(pair[1]) : null;

  return (
    <div className="rr2-wrap">
      <h2 className="rr2-title">{listTitle}</h2>

      {!done && a && b ? (
        <div className="rr2-stage" role="group" aria-label="Choose which you prefer">
          <button type="button" className="rr2-option" onClick={() => choose(a.id, b.id)}>
            {a.title}
          </button>
          <span style={{ margin: '0 10px', fontWeight: 700, color: '#6b7280' }}>vs.</span>
          <button type="button" className="rr2-option" onClick={() => choose(b.id, a.id)}>
            {b.title}
          </button>
        </div>
      ) : (
        <div className="rr2-pending">Finishing…</div>
      )}
    </div>
  );
};

export default RoundRobinGame;
